/**
 * Calendar & Diary — Wave 2: the full diary page at /agent/calendar
 * (CALENDAR_DIARY_DESIGN §3.3, wave-2 line of §5).
 *
 * Outlook-parity layout: left rail with two mini-months + a per-kind
 * "My Calendars" filter; main Day / Week / Month / Schedule switcher;
 * Today + ‹ › pager; Previous / Next Appointment jumps; keyboard d/w/m/s
 * and ←/→. Data comes from the wave-1 API — getCalendarEvents over the
 * visible range plus one view-width of margin so paging is instant; kind
 * filtering is client-side.
 *
 * Quick actions: Reschedule + Cancel (wave-1 PATCH path) plus the §3.4
 * actions — Send message (deep-link into Inbox with a prefilled draft),
 * Reassign and Delegate (POST routes via MemberActionDialog).
 *
 * Honesty: empty / loading / failed-fetch / module-disabled states all
 * render real copy (the wave-1 widget pattern), never spinners-forever.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MessageSquare,
  Plus,
  Settings,
  SkipBack,
  SkipForward,
  UserRoundCheck,
  UserRoundPlus,
  Users,
  XCircle,
} from 'lucide-react'
import { api } from '@/api/client'
import {
  KIND_COLORS,
  memberColor,
  type CalendarEvent,
  type CalendarKind,
  type CalendarTeamEventsResponse,
  type TeamCalendarEvent,
} from '@/api/calendar'
import { useToast } from '@/components/ui/toast'
import { useAuth } from '@/context/AuthContext'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { t, type DashboardLocale } from '@/pages/agent/dashboard/copy'
import { useLocale } from '@/hooks/useLocale'
import {
  CALENDAR_KINDS,
  KIND_ICONS,
  SeriesGlyph,
  dayKey,
  eventsOnDay,
  fetchWindowFor,
  groupEventsByDay,
  isMultiDay,
  monthCellsFor,
  startOfDay,
  toLocalInputValue,
  weekDaysFor,
} from './shared'
import { AddAppointmentDialog, CancelEventDialog, RescheduleForm, type AddPrefill } from './AppointmentDialogs'
import { CalendarSettingsDialog } from './CalendarSettingsDialog'
import { MemberActionDialog, type MemberActionMode } from './MemberActionDialog'

type DiaryView = 'day' | 'week' | 'month' | 'schedule'

const VIEW_KEYS: Array<[DiaryView, Parameters<typeof t>[0]]> = [
  ['day', 'calendar.view.day'],
  ['week', 'calendar.view.week'],
  ['month', 'calendar.view.month'],
  ['schedule', 'calendar.view.schedule'],
]

/** Hour grid bounds for day/week views. */
const GRID_START_MIN = 7 * 60
const GRID_END_MIN = 21 * 60

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  return String(err)
}

function parseDayParam(value: string | null): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const d = new Date(`${value}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

export function CalendarDiaryPage() {
  const { locale, dir } = useLocale()
  const { addToast } = useToast()
  const { agent } = useAuth()
  const [searchParams] = useSearchParams()

  const [view, setView] = useState<DiaryView>('month')
  const [anchor, setAnchor] = useState<Date>(() => parseDayParam(searchParams.get('day')) ?? startOfDay(new Date()))
  const [hiddenKinds, setHiddenKinds] = useState<Set<CalendarKind>>(new Set())
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [prefill, setPrefill] = useState<AddPrefill>(null)
  const [rescheduleTarget, setRescheduleTarget] = useState<CalendarEvent | null>(null)
  const [memberAction, setMemberAction] = useState<{ event: CalendarEvent; mode: MemberActionMode; targetId?: string } | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [ruleError, setRuleError] = useState<string | null>(null)
  const [cancelTarget, setCancelTarget] = useState<CalendarEvent | null>(null)
  const [patchBusy, setPatchBusy] = useState(false)
  // Wave 6b — bumps the fetch effect so whole-series edits re-expand the
  // window from the server (the PATCH response is the parent row, not the
  // new occurrence set).
  const [refetchTick, setRefetchTick] = useState(0)
  // Wave 5 — team calendar: data is fetched for the day/week window so the
  // toggle can render its honest disabled state; rendering is gated on teamOn.
  const [teamOn, setTeamOn] = useState(false)
  const [teamData, setTeamData] = useState<CalendarTeamEventsResponse | null>(null)
  const [teamError, setTeamError] = useState(false)
  const mainRef = useRef<HTMLDivElement>(null)

  // Fetch the visible range + one view-width margin; refetch on view/anchor.
  useEffect(() => {
    let cancelled = false
    const window = fetchWindowFor(view, anchor)
    setLoading(true)
    api
      .getCalendarEvents({
        from: window.from.toISOString(),
        to: window.to.toISOString(),
        kinds: [...CALENDAR_KINDS],
      })
      .then((res) => {
        if (cancelled) return
        setEvents(res?.events ?? [])
        setError(false)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setEvents([])
        setError(true)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [view, anchor, refetchTick])

  // Team calendar (wave 5): same fetch window as own events, day/week only.
  // Fetched up-front (not on toggle) so a solo agency renders the toggle
  // disabled with its honest reason instead of a dead switch.
  useEffect(() => {
    if (view !== 'day' && view !== 'week') return
    let cancelled = false
    const window = fetchWindowFor(view, anchor)
    api
      .getCalendarTeamEvents({
        from: window.from.toISOString(),
        to: window.to.toISOString(),
      })
      .then((res) => {
        if (cancelled) return
        setTeamData({ events: res?.events ?? [], members: res?.members ?? [] })
        setTeamError(false)
      })
      .catch(() => {
        if (cancelled) return
        setTeamData(null)
        setTeamError(true)
      })
    return () => {
      cancelled = true
    }
  }, [view, anchor])

  const visibleEvents = useMemo(
    () => events.filter((event) => !hiddenKinds.has(event.kind)),
    [events, hiddenKinds],
  )
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>()
    for (const [key, group] of groupEventsByDay(events)) map.set(key, group)
    return map
  }, [events])

  // Team events that are not the user's own: own events render through the
  // normal path already, so dedupe by id and drop the user's own owner id.
  const ownUserId = agent?.id
  const teamSolo = teamData !== null && teamData.members.length === 0
  const teamEvents = useMemo(() => {
    if (!teamOn || !teamData) return []
    const ownIds = new Set(events.map((e) => e.id))
    return teamData.events.filter(
      (e) => !ownIds.has(e.id) && (!ownUserId || e.owner_user_id !== ownUserId),
    )
  }, [teamOn, teamData, events, ownUserId])

  const todayKey = dayKey(new Date())
  const anchorKey = dayKey(anchor)

  const page = (delta: number) => {
    setAnchor((prev) => {
      const next = new Date(prev)
      if (view === 'day') next.setDate(next.getDate() + delta)
      else if (view === 'week' || view === 'schedule') next.setDate(next.getDate() + 7 * delta)
      else {
        next.setMonth(next.getMonth() + delta, 1)
      }
      return next
    })
  }

  const jumpToAppointment = (direction: -1 | 1) => {
    const sorted = [...visibleEvents]
      .filter((e) => e.status !== 'cancelled')
      .sort((a, b) => (a.starts_at < b.starts_at ? -1 : 1))
    const boundary = anchor.getTime()
    const target =
      direction === 1
        ? sorted.find((e) => new Date(e.starts_at).getTime() > boundary)
        : [...sorted].reverse().find((e) => new Date(e.starts_at).getTime() < boundary)
    if (target) setAnchor(startOfDay(new Date(target.starts_at)))
  }

  // Keyboard: d/w/m/s switch views, ←/→ page (ignored while typing).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const tag = (event.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      const key = event.key.toLowerCase()
      if (key === 'd') setView('day')
      else if (key === 'w') setView('week')
      else if (key === 'm') setView('month')
      else if (key === 's') setView('schedule')
      else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        page(-1)
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        page(1)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  /**
   * Optimistic PATCH. Wave 6b: a series target PATCHes the series id
   * (whole-series v1 — never the derived occurrence id, which 404s). Local
   * reconciliation shifts/marks every loaded occurrence of the series, then a
   * refetch re-expands the window from the server as the authority.
   */
  const patchEvent = async (target: CalendarEvent, patch: Record<string, unknown>, okKey: Parameters<typeof t>[0]) => {
    if (patchBusy) return
    setPatchBusy(true)
    const seriesId = target.series_id ?? null
    const prevEvents = events
    const shiftMs =
      seriesId && typeof patch.starts_at === 'string' && !Number.isNaN(new Date(patch.starts_at).getTime())
        ? new Date(patch.starts_at as string).getTime() - new Date(target.starts_at).getTime()
        : 0
    const applySeriesPatch = (e: CalendarEvent): CalendarEvent => {
      if (e.series_id !== seriesId) return e
      if (e.id === target.id) return { ...e, ...(patch as Partial<CalendarEvent>) }
      if (shiftMs !== 0) {
        const start = new Date(e.starts_at).getTime() + shiftMs
        const end = e.ends_at ? new Date(e.ends_at).getTime() + shiftMs : null
        return { ...e, starts_at: new Date(start).toISOString(), ends_at: end === null ? null : new Date(end).toISOString() }
      }
      return { ...e, ...(patch as Partial<CalendarEvent>) }
    }
    const optimistic: CalendarEvent = { ...target, ...(patch as Partial<CalendarEvent>) }
    setEvents((prev) =>
      seriesId ? prev.map(applySeriesPatch) : prev.map((e) => (e.id === target.id ? optimistic : e)),
    )
    try {
      const { event } = await api.updateCalendarEvent(seriesId ?? target.id, patch)
      if (seriesId) {
        setRefetchTick((n) => n + 1)
      } else {
        setEvents((prev) => prev.map((e) => (e.id === target.id ? event : e)))
      }
      addToast({ title: t(okKey, locale), variant: 'success' })
      setRescheduleTarget(null)
      setCancelTarget(null)
    } catch (err) {
      setEvents(prevEvents)
      setRuleError(errorMessage(err))
      addToast({
        title: t('calendar.toast.updateError', locale),
        description: errorMessage(err),
        variant: 'error',
      })
    } finally {
      setPatchBusy(false)
    }
  }

  /** Wave 6b — cancel ONE occurrence: POST /:series_id/cancel-occurrence. */
  const cancelOccurrence = async (target: CalendarEvent) => {
    if (patchBusy) return
    setPatchBusy(true)
    const prevEvents = events
    // Cancelled occurrences are absent from the expansion — drop it locally.
    setEvents((prev) => prev.filter((e) => e.id !== target.id))
    try {
      await api.cancelCalendarOccurrence(target.series_id ?? target.id, target.occurrence_start ?? target.starts_at)
      addToast({ title: t('calendar.toast.cancelled', locale), variant: 'success' })
      setCancelTarget(null)
      setRefetchTick((n) => n + 1)
    } catch (err) {
      setEvents(prevEvents)
      setRuleError(errorMessage(err))
      addToast({
        title: t('calendar.toast.cancelError', locale),
        description: errorMessage(err),
        variant: 'error',
      })
    } finally {
      setPatchBusy(false)
    }
  }

  /** Cancel dialog confirm: occurrence-only for instances, PATCH otherwise. */
  const confirmCancel = (scope: 'occurrence' | 'series') => {
    const target = cancelTarget
    if (!target) return
    if (scope === 'occurrence' && (target.is_recurrence_instance || target.series_id)) {
      void cancelOccurrence(target)
    } else {
      void patchEvent(target, { status: 'cancelled' }, 'calendar.toast.cancelled')
    }
  }

  /** §3.4 Reassign / Delegate: POSTs target the series id for instances. */
  const openMemberAction = (event: CalendarEvent, mode: MemberActionMode) =>
    setMemberAction({ event, mode, targetId: event.series_id ?? undefined })

  const navigate = useNavigate()

  const applyEventUpdate = (updated: CalendarEvent) => {
    setEvents((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)))
  }

  /**
   * §3.4 Send message: open the conversation with the linked contact,
   * prefilled with the event context. Nothing is written to the event.
   * Without a linked contact the button renders disabled instead.
   */
  const sendMessageTo = async (event: CalendarEvent) => {
    if (!event.contact_id) {
      addToast({ title: t('calendar.noLinkedContact', locale), variant: 'error' })
      return
    }
    const context = `${event.title} · ${new Intl.DateTimeFormat(locale, {
      day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
    }).format(new Date(event.starts_at))}`
    try {
      const conversations = await api.getConversations()
      const match = (conversations || []).find((c) => c.contact_id === event.contact_id)
      if (match) {
        navigate(`/inbox?conversation=${encodeURIComponent(match.id)}&draft=${encodeURIComponent(context)}`)
      } else {
        navigate('/inbox')
        addToast({ title: t('calendar.sendMessage.noConversation', locale), variant: 'default' })
      }
    } catch {
      navigate('/inbox')
    }
  }

  /** Drag-to-reschedule (week/day grids): preserves duration; rolls back on rejection. */
  const rescheduleEventTo = async (event: CalendarEvent, newStart: Date) => {
    const originalStart = new Date(event.starts_at)
    const durationMs = event.ends_at
      ? new Date(event.ends_at).getTime() - originalStart.getTime()
      : 60 * 60_000
    const newEnd = new Date(newStart.getTime() + durationMs)
    setRuleError(null)
    // Wave 6b: dragging a series instance moves the whole series (v1) — the
    // API targets the series id; sibling occurrences shift by the same delta.
    const seriesId = event.series_id ?? null
    const shiftMs = newStart.getTime() - originalStart.getTime()
    const prevEvents = events
    setEvents((prev) =>
      seriesId
        ? prev.map((e) => {
            if (e.series_id !== seriesId) return e
            if (e.id === event.id) {
              return { ...e, starts_at: newStart.toISOString(), ends_at: newEnd.toISOString() }
            }
            const start = new Date(e.starts_at).getTime() + shiftMs
            const end = e.ends_at ? new Date(e.ends_at).getTime() + shiftMs : null
            return { ...e, starts_at: new Date(start).toISOString(), ends_at: end === null ? null : new Date(end).toISOString() }
          })
        : prev.map((e) => (e.id === event.id
            ? { ...e, starts_at: newStart.toISOString(), ends_at: newEnd.toISOString() }
            : e)),
    )
    try {
      const { event: updated } = await api.updateCalendarEvent(seriesId ?? event.id, {
        starts_at: newStart.toISOString(),
        ends_at: newEnd.toISOString(),
      })
      if (seriesId) {
        setRefetchTick((n) => n + 1)
      } else {
        applyEventUpdate(updated)
      }
    } catch (err) {
      setEvents(prevEvents)
      setRuleError(errorMessage(err))
      addToast({ title: t('calendar.toast.updateError', locale), description: errorMessage(err), variant: 'error' })
    }
  }

  const openAdd = (next: AddPrefill = null) => {
    setPrefill(next)
    setAddOpen(true)
  }

  const openAddAt = (day: Date, hour: number) => {
    openAdd({ kind: 'meeting', when: toLocalInputValue(new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0)) })
  }

  const weekdayLabels = useMemo(() => {
    // Monday-start (product convention); 2024-01-01 was a Monday.
    const base = new Date(2024, 0, 1)
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base)
      d.setDate(base.getDate() + i)
      return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d)
    })
  }, [locale])

  const periodLabel = useMemo(() => {
    if (view === 'day') {
      return new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(anchor)
    }
    if (view === 'week') {
      const days = weekDaysFor(anchor)
      const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' })
      return `${fmt.format(days[0])} – ${fmt.format(days[6])}`
    }
    return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(anchor)
  }, [view, anchor, locale])

  const railMonths = useMemo(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
    const second = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1)
    return [first, second]
  }, [anchor])

  const renderState = (testId: string, messageKey: Parameters<typeof t>[0]) => (
    <div className="flex min-h-64 flex-col items-center justify-center gap-[var(--lc-space-sm)]" data-testid={testId}>
      {loading ? <Loader2 className="h-5 w-5 animate-spin text-[var(--lc-text-muted)]" aria-hidden="true" /> : null}
      <p className="text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-body-sm)' }}>
        {t(messageKey, locale)}
      </p>
    </div>
  )

  const renderMain = () => {
    if (error) return renderState('diary-error', 'calendar.error')
    if (loading && events.length === 0) return renderState('diary-loading', 'calendar.loading')
    if (view === 'schedule') {
      const window = fetchWindowFor('schedule', anchor)
      const inWindow = visibleEvents.filter((e) => {
        const d = new Date(e.starts_at)
        return d >= window.from && d < window.to
      })
      if (inWindow.length === 0) return renderState('diary-empty', 'calendar.empty')
      return (
        <ScheduleList
          groups={groupEventsByDay(inWindow)}
          locale={locale}
          onReschedule={setRescheduleTarget}
          onCancel={setCancelTarget}
          onSendMessage={(event) => void sendMessageTo(event)}
          onMemberAction={openMemberAction}
        />
      )
    }
    if (view === 'month') return (
      <MonthGrid
        year={anchor.getFullYear()}
        month={anchor.getMonth()}
        eventsByDay={eventsByDay}
        hiddenKinds={hiddenKinds}
        todayKey={todayKey}
        anchorKey={anchorKey}
        weekdayLabels={weekdayLabels}
        onSelectDay={(day) => {
          setAnchor(new Date(anchor.getFullYear(), anchor.getMonth(), day))
          setView('day')
        }}
      />
    )
    if (view === 'week') {
      const days = weekDaysFor(anchor)
      const weekEvents = visibleEvents.filter((e) => {
        const key = dayKey(new Date(e.starts_at))
        return days.some((d) => dayKey(d) === key)
      })
      return (
        <>
          {teamOn ? (
            <TeamStrip
              members={(teamData?.members ?? []).filter((m) => m.user_id !== ownUserId)}
              events={teamEvents}
              anchorKey={anchorKey}
              locale={locale}
              failed={teamError}
            />
          ) : null}
          <TimeGrid
            days={days}
            events={weekEvents}
            teamEvents={teamEvents}
            locale={locale}
            weekdayLabels={weekdayLabels}
            todayKey={todayKey}
            emptyTestId="diary-empty-week"
            emptyKey="calendar.empty"
            onCellClick={openAddAt}
            onReschedule={setRescheduleTarget}
            onCancel={setCancelTarget}
            onSendMessage={(event) => void sendMessageTo(event)}
            onMemberAction={openMemberAction}
            onDropEvent={(eventId, day, hour) => {
              const event = events.find((e) => e.id === eventId)
              if (event) void rescheduleEventTo(event, new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0))
            }}
          />
        </>
      )
    }
    const dayEvents = eventsOnDay(visibleEvents, anchorKey)
    return (
      <>
        {teamOn ? (
          <TeamStrip
            members={(teamData?.members ?? []).filter((m) => m.user_id !== ownUserId)}
            events={teamEvents}
            anchorKey={anchorKey}
            locale={locale}
            failed={teamError}
          />
        ) : null}
        <TimeGrid
          days={[startOfDay(anchor)]}
          events={dayEvents}
          teamEvents={teamEvents}
          locale={locale}
          weekdayLabels={weekdayLabels}
          todayKey={todayKey}
          emptyTestId="diary-empty-day"
          emptyKey="calendar.emptyDay"
          onCellClick={openAddAt}
          onReschedule={setRescheduleTarget}
          onCancel={setCancelTarget}
          onSendMessage={(event) => void sendMessageTo(event)}
          onMemberAction={openMemberAction}
          onDropEvent={(eventId, day, hour) => {
            const event = events.find((e) => e.id === eventId)
            if (event) void rescheduleEventTo(event, new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0))
          }}
        />
      </>
    )
  }

  return (
    <main data-testid="calendar-diary" data-dir={dir} className="flex min-h-0 flex-1 flex-col gap-[var(--lc-space-md)] p-[var(--lc-space-md)]">
      <header className="flex flex-wrap items-center gap-[var(--lc-space-sm)]">
        <h1 className="text-[var(--lc-text-heading)]" style={{ font: 'var(--lc-type-heading-sm)' }}>
          {t('calendar.diary.title', locale)}
        </h1>
        <div
          role="tablist"
          aria-label={t('calendar.diary.title', locale)}
          className="inline-flex rounded-[var(--lc-radius-md)] border border-[var(--lc-border)] bg-[var(--lc-surface-sunken)] p-0.5"
        >
          {VIEW_KEYS.map(([value, key]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={view === value}
              onClick={() => setView(value)}
              className={cn(
                'min-h-tap rounded-[var(--lc-radius-sm)] px-3 transition-colors',
                view === value
                  ? 'bg-[var(--lc-surface-raised)] text-[var(--lc-text-primary)] shadow-[var(--lc-elevation-sm)]'
                  : 'text-[var(--lc-text-muted)] hover:text-[var(--lc-text-primary)]',
              )}
              style={{ font: 'var(--lc-type-caption)' }}
            >
              {t(key, locale)}
            </button>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={t('calendar.settings.open', locale)}
          title={t('calendar.settings.open', locale)}
          onClick={() => setSettingsOpen(true)}
        >
          <Settings className="h-4 w-4" aria-hidden="true" />
        </Button>
        <span className="text-[var(--lc-text-secondary)]" style={{ font: 'var(--lc-type-body-sm)' }}>
          {periodLabel}
        </span>
        <span className="ms-auto flex items-center gap-[var(--lc-space-2xs)]">
          <Button type="button" variant="outline" size="sm" onClick={() => setAnchor(startOfDay(new Date()))}>
            {t('calendar.pager.today', locale)}
          </Button>
          <Button type="button" variant="outline" size="icon" aria-label={t('calendar.pager.previous', locale)} title={t('calendar.pager.previous', locale)} onClick={() => page(-1)}>
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button type="button" variant="outline" size="icon" aria-label={t('calendar.pager.next', locale)} title={t('calendar.pager.next', locale)} onClick={() => page(1)}>
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button type="button" variant="outline" size="icon" aria-label={t('calendar.jump.previous', locale)} title={t('calendar.jump.previous', locale)} onClick={() => jumpToAppointment(-1)}>
            <SkipBack className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button type="button" variant="outline" size="icon" aria-label={t('calendar.jump.next', locale)} title={t('calendar.jump.next', locale)} onClick={() => jumpToAppointment(1)}>
            <SkipForward className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t('calendar.add', locale)}
            title={t('calendar.add', locale)}
            onClick={() => openAdd()}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
          </Button>
        </span>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-[var(--lc-space-md)] md:flex-row">
        <aside className="flex w-full shrink-0 flex-col gap-[var(--lc-space-md)] md:w-56" aria-label={t('calendar.calendars.my', locale)}>
          {railMonths.map((monthDate) => (
            <MiniMonth
              key={dayKey(monthDate)}
              year={monthDate.getFullYear()}
              month={monthDate.getMonth()}
              eventsByDay={eventsByDay}
              hiddenKinds={hiddenKinds}
              todayKey={todayKey}
              anchorKey={anchorKey}
              locale={locale}
              weekdayLabels={weekdayLabels}
              onSelectDay={(day) => setAnchor(new Date(monthDate.getFullYear(), monthDate.getMonth(), day))}
            />
          ))}
          <section>
            <h2 className="mb-1 text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-overline)', letterSpacing: 'var(--lc-tracking-overline)' }}>
              {t('calendar.calendars.my', locale)}
            </h2>
            <ul>
              {CALENDAR_KINDS.map((kind) => {
                const KindIcon = KIND_ICONS[kind]
                const hidden = hiddenKinds.has(kind)
                return (
                  <li key={kind}>
                    <label className="flex min-h-tap cursor-pointer items-center gap-[var(--lc-space-sm)]">
                      <input
                        type="checkbox"
                        checked={!hidden}
                        onChange={() =>
                          setHiddenKinds((prev) => {
                            const next = new Set(prev)
                            if (next.has(kind)) next.delete(kind)
                            else next.add(kind)
                            return next
                          })
                        }
                        aria-label={t(`calendar.kind.${kind}` as Parameters<typeof t>[0], locale)}
                      />
                      <KindIcon className="h-4 w-4" style={{ color: KIND_COLORS[kind] }} aria-hidden="true" />
                      <span style={{ font: 'var(--lc-type-body-sm)' }}>{t(`calendar.kind.${kind}` as Parameters<typeof t>[0], locale)}</span>
                    </label>
                  </li>
                )
              })}
            </ul>
          </section>
          {view === 'day' || view === 'week' ? (
            <section data-testid="team-toggle-section">
              <h2 className="mb-1 text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-overline)', letterSpacing: 'var(--lc-tracking-overline)' }}>
                {t('calendar.team.section', locale)}
              </h2>
              <label className="flex min-h-tap cursor-pointer items-center gap-[var(--lc-space-sm)]">
                <input
                  type="checkbox"
                  checked={teamOn}
                  disabled={teamSolo}
                  onChange={(e) => setTeamOn(e.target.checked)}
                  aria-label={t('calendar.team.toggle', locale)}
                />
                <Users className="h-4 w-4" style={{ color: 'var(--lc-channel-linkedin)' }} aria-hidden="true" />
                <span style={{ font: 'var(--lc-type-body-sm)' }}>{t('calendar.team.toggle', locale)}</span>
              </label>
              {teamSolo ? (
                <p data-testid="team-solo-note" className="mt-1 text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
                  {t('calendar.team.soloDisabled', locale)}
                </p>
              ) : null}
              {teamError ? (
                <p data-testid="team-error-note" role="alert" className="mt-1 text-[var(--lc-status-unpublished-fg)]" style={{ font: 'var(--lc-type-caption)' }}>
                  {t('calendar.team.error', locale)}
                </p>
              ) : null}
            </section>
          ) : null}
        </aside>

        <div ref={mainRef} className="min-h-0 flex-1 overflow-y-auto" tabIndex={-1}>
          {ruleError ? (
            <p
              data-testid="rule-error"
              role="alert"
              className="mb-[var(--lc-space-sm)] rounded-[var(--lc-radius-sm)] border border-[var(--lc-status-unpublished-fg)] px-2 py-1 text-[var(--lc-status-unpublished-fg)]"
              style={{ font: 'var(--lc-type-body-sm)' }}
            >
              {t('calendar.ruleError.title', locale)} {ruleError}
            </p>
          ) : null}
          {renderMain()}
        </div>
      </div>

      <p className="text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
        {t('calendar.keyboardHint', locale)}
      </p>

      <AddAppointmentDialog
        key={`${addOpen ? 'open' : 'closed'}-${prefill?.when ?? ''}-${prefill?.kind ?? ''}`}
        open={addOpen}
        onOpenChange={setAddOpen}
        prefill={prefill}
        locale={locale}
        onCreated={(event) => {
          setEvents((prev) => [...prev, event])
          setAddOpen(false)
        }}
      />

      <Dialog open={rescheduleTarget !== null} onOpenChange={(open) => !open && setRescheduleTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('calendar.reschedule.title', locale)}</DialogTitle>
          </DialogHeader>
          {rescheduleTarget ? (
            <RescheduleForm
              event={rescheduleTarget}
              locale={locale}
              busy={patchBusy}
              onCancel={() => setRescheduleTarget(null)}
              onSubmit={(patch) => rescheduleTarget && patchEvent(rescheduleTarget, patch, 'calendar.toast.updated')}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <MemberActionDialog
        event={memberAction?.event ?? null}
        mode={memberAction?.mode ?? 'reassign'}
        targetId={memberAction?.targetId}
        locale={locale}
        onClose={() => setMemberAction(null)}
        onApplied={(updated) => {
          // Whole-series v1: the response is the parent row — re-expand the
          // window instead of mapping an id no loaded occurrence carries.
          if (memberAction?.targetId) setRefetchTick((n) => n + 1)
          else applyEventUpdate(updated)
        }}
      />

      <CalendarSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} locale={locale} />

      <CancelEventDialog
        event={cancelTarget}
        locale={locale}
        busy={patchBusy}
        onClose={() => setCancelTarget(null)}
        onConfirm={confirmCancel}
      />
    </main>
  )
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function MiniMonth({
  year,
  month,
  eventsByDay,
  hiddenKinds,
  todayKey,
  anchorKey,
  locale,
  weekdayLabels,
  onSelectDay,
}: {
  year: number
  month: number
  eventsByDay: Map<string, CalendarEvent[]>
  hiddenKinds: Set<CalendarKind>
  todayKey: string
  anchorKey: string
  locale: DashboardLocale
  weekdayLabels: string[]
  onSelectDay: (day: number) => void
}) {
  const label = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(year, month, 1))
  return (
    <section data-testid="mini-month" aria-label={label}>
      <h3 className="mb-1 text-[var(--lc-text-heading)]" style={{ font: 'var(--lc-type-body-sm)' }}>
        {label}
      </h3>
      <div className="grid grid-cols-7 gap-0.5">
        {weekdayLabels.map((label, i) => (
          <span key={`${label}-${i}`} className="text-center text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
            {label.slice(0, 2)}
          </span>
        ))}
        {monthCellsFor(year, month).map((cell, i) => {
          if (cell.day === null) return <span key={`blank-${i}`} />
          const key = dayKey(new Date(year, month, cell.day))
          const dayEvents = (eventsByDay.get(key) ?? []).filter((e) => !hiddenKinds.has(e.kind))
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelectDay(cell.day as number)}
              aria-label={`${key}${dayEvents.length ? ` · ${dayEvents.length}` : ''}`}
              className={cn(
                'flex min-h-tap flex-col items-center rounded-[var(--lc-radius-sm)] p-0.5 hover:bg-[var(--lc-surface-sunken)]',
                key === todayKey && 'ring-1 ring-[var(--lc-action-primary)]',
                key === anchorKey && 'bg-[var(--lc-surface-selected)]',
              )}
            >
              <span className="tabular-nums" style={{ font: 'var(--lc-type-caption)' }}>
                {cell.day}
              </span>
              <span className="flex h-1.5 items-center gap-0.5" aria-hidden="true">
                {dayEvents.slice(0, 3).map((event) => (
                  <span key={event.id} className="h-1 w-1 rounded-pill" style={{ backgroundColor: KIND_COLORS[event.kind] }} />
                ))}
              </span>
            </button>
          )
        })}
      </div>
    </section>
  )
}

function MonthGrid({
  year,
  month,
  eventsByDay,
  hiddenKinds,
  todayKey,
  anchorKey,
  weekdayLabels,
  onSelectDay,
}: {
  year: number
  month: number
  eventsByDay: Map<string, CalendarEvent[]>
  hiddenKinds: Set<CalendarKind>
  todayKey: string
  anchorKey: string
  weekdayLabels: string[]
  onSelectDay: (day: number) => void
}) {
  const cells = monthCellsFor(year, month)
  return (
    <div data-testid="diary-month" className="grid grid-cols-7 gap-1">
      {weekdayLabels.map((label) => (
        <span key={label} className="text-center text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
          {label}
        </span>
      ))}
      {cells.map((cell, i) => {
        if (cell.day === null) return <span key={`blank-${i}`} />
        const key = dayKey(new Date(year, month, cell.day))
        const dayEvents = (eventsByDay.get(key) ?? []).filter((e) => !hiddenKinds.has(e.kind))
        const extra = dayEvents.length - 3
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelectDay(cell.day as number)}
            aria-label={`${key}${dayEvents.length ? ` · ${dayEvents.length}` : ''}`}
            className={cn(
              'flex min-h-16 flex-col items-center gap-1 rounded-[var(--lc-radius-sm)] p-1 hover:bg-[var(--lc-surface-sunken)]',
              key === todayKey && 'ring-1 ring-[var(--lc-action-primary)]',
              key === anchorKey && 'bg-[var(--lc-surface-selected)]',
            )}
          >
            <span className="tabular-nums" style={{ font: 'var(--lc-type-caption)' }}>
              {cell.day}
            </span>
            <span className="flex h-3 items-center gap-0.5" aria-hidden="true">
              {dayEvents.slice(0, 3).map((event) => (
                <span key={event.id} className="h-1.5 w-1.5 rounded-pill" style={{ backgroundColor: KIND_COLORS[event.kind] }} />
              ))}
              {extra > 0 ? (
                <span className="text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
                  +{extra}
                </span>
              ) : null}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function TimeGrid({
  days,
  events,
  teamEvents,
  locale,
  weekdayLabels,
  todayKey,
  emptyTestId,
  emptyKey,
  onCellClick,
  onReschedule,
  onCancel,
  onSendMessage,
  onMemberAction,
  onDropEvent,
}: {
  days: Date[]
  events: CalendarEvent[]
  teamEvents?: TeamCalendarEvent[]
  locale: DashboardLocale
  weekdayLabels: string[]
  todayKey: string
  emptyTestId: string
  emptyKey: Parameters<typeof t>[0]
  onCellClick: (day: Date, hour: number) => void
  onReschedule: (event: CalendarEvent) => void
  onCancel: (event: CalendarEvent) => void
  onSendMessage: (event: CalendarEvent) => void
  onMemberAction: (event: CalendarEvent, mode: MemberActionMode) => void
  onDropEvent: (eventId: string, day: Date, hour: number) => void
}) {
  const hours = useMemo(() => {
    const list: number[] = []
    for (let h = GRID_START_MIN / 60; h < GRID_END_MIN / 60; h++) list.push(h)
    return list
  }, [])
  const spanMinutes = GRID_END_MIN - GRID_START_MIN

  const singleDay = days.length === 1

  return (
    <div data-testid={singleDay ? 'diary-day' : 'diary-week'} className="overflow-x-auto">
      {events.length === 0 && (teamEvents?.length ?? 0) === 0 ? (
        <p className="mb-1 text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-body-sm)' }} data-testid={emptyTestId}>
          {t(emptyKey, locale)}
        </p>
      ) : null}
      <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(9rem, 1fr))` }}>
        {days.map((day) => (
          <div key={dayKey(day)} className="min-w-0">
            <div className="mb-1 text-center" style={{ font: 'var(--lc-type-caption)' }}>
              <span className="text-[var(--lc-text-muted)]">{weekdayLabels[(day.getDay() + 6) % 7]}</span>{' '}
              <span
                className={cn('tabular-nums', dayKey(day) === todayKey ? 'rounded-pill px-1.5 py-0.5 text-[var(--lc-action-primary-text)]' : 'text-[var(--lc-text-heading)]')}
                style={dayKey(day) === todayKey ? { backgroundColor: 'var(--lc-action-primary)' } : undefined}
              >
                {day.getDate()}
              </span>
            </div>
            {/* All-day / chip row: multi-day events render as chips — honest, no fake all-day math. */}
            <div className="mb-1 min-h-6 rounded-[var(--lc-radius-sm)] bg-[var(--lc-surface-sunken)] p-0.5">
              {events
                .filter((e) => isMultiDay(e) || e.all_day)
                .filter((e) => {
                  if (e.ends_at == null) return dayKey(new Date(e.starts_at)) === dayKey(day)
                  const start = new Date(e.starts_at)
                  const end = new Date(e.ends_at)
                  const d = startOfDay(day).getTime()
                  return startOfDay(start).getTime() <= d && end.getTime() >= d
                })
                .map((event) => (
                  <EventChip
                    key={event.id}
                    event={event}
                    locale={locale}
                    onReschedule={onReschedule}
                    onCancel={onCancel}
                    onSendMessage={onSendMessage}
                    onMemberAction={onMemberAction}
                  />
                ))}
              {(teamEvents ?? [])
                .filter((e) => isMultiDay(e) || e.all_day)
                .filter((e) => {
                  if (e.ends_at == null) return dayKey(new Date(e.starts_at)) === dayKey(day)
                  const start = new Date(e.starts_at)
                  const end = new Date(e.ends_at)
                  const d = startOfDay(day).getTime()
                  return startOfDay(start).getTime() <= d && end.getTime() >= d
                })
                .map((event) => (
                  <TeamEventChip key={event.id} event={event} locale={locale} />
                ))}
            </div>
            <div className="relative" style={{ height: `${(spanMinutes / 60) * 3}rem` }}>
              {hours.map((hour) => (
                <button
                  key={hour}
                  type="button"
                  onClick={() => onCellClick(day, hour)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault()
                    const eventId = e.dataTransfer.getData('text/calendar-event')
                    if (eventId) onDropEvent(eventId, day, hour)
                  }}
                  aria-label={`${dayKey(day)} ${String(hour).padStart(2, '0')}:00`}
                  className="absolute inset-x-0 border-t border-[var(--lc-border)] text-start hover:bg-[var(--lc-surface-sunken)]"
                  style={{ top: `${((hour * 60 - GRID_START_MIN) / spanMinutes) * 100}%`, height: `${(60 / spanMinutes) * 100}%` }}
                >
                  <span className="sr-only">
                    {new Intl.DateTimeFormat(locale, { hour: 'numeric' }).format(new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0))}
                  </span>
                </button>
              ))}
              {events
                .filter((e) => !isMultiDay(e) && !e.all_day)
                .filter((e) => dayKey(new Date(e.starts_at)) === dayKey(day))
                .map((event) => (
                  <EventBlock
                    key={event.id}
                    event={event}
                    spanMinutes={spanMinutes}
                    locale={locale}
                    onReschedule={onReschedule}
                    onCancel={onCancel}
                    onSendMessage={onSendMessage}
                    onMemberAction={onMemberAction}
                  />
                ))}
              {(teamEvents ?? [])
                .filter((e) => !isMultiDay(e) && !e.all_day)
                .filter((e) => dayKey(new Date(e.starts_at)) === dayKey(day))
                .map((event) => (
                  <TeamEventBlock key={event.id} event={event} spanMinutes={spanMinutes} locale={locale} />
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function eventPosition(event: CalendarEvent, spanMinutes: number): { top: string; height: string } {
  const start = new Date(event.starts_at)
  const end = event.ends_at ? new Date(event.ends_at) : null
  const startMin = start.getHours() * 60 + start.getMinutes()
  const endMin = end && !Number.isNaN(end.getTime()) ? end.getHours() * 60 + end.getMinutes() : startMin + 60
  const clampedStart = Math.max(GRID_START_MIN, Math.min(startMin, GRID_END_MIN))
  const clampedEnd = Math.max(clampedStart + 15, Math.min(endMin, GRID_END_MIN))
  return {
    top: `${((clampedStart - GRID_START_MIN) / spanMinutes) * 100}%`,
    height: `${((clampedEnd - clampedStart) / spanMinutes) * 100}%`,
  }
}

function EventBlock({
  event,
  spanMinutes,
  locale,
  onReschedule,
  onCancel,
  onSendMessage,
  onMemberAction,
}: {
  event: CalendarEvent
  spanMinutes: number
  locale: DashboardLocale
  onReschedule: (event: CalendarEvent) => void
  onCancel: (event: CalendarEvent) => void
  onSendMessage: (event: CalendarEvent) => void
  onMemberAction: (event: CalendarEvent, mode: MemberActionMode) => void
}) {
  const KindIcon = KIND_ICONS[event.kind] ?? KIND_ICONS.custom
  const colour = KIND_COLORS[event.kind]
  const cancelled = event.status === 'cancelled'
  const pos = eventPosition(event, spanMinutes)
  const start = new Date(event.starts_at)
  return (
    <div
      data-testid={`event-${event.id}`}
      draggable={!cancelled}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/calendar-event', event.id)
        e.dataTransfer.effectAllowed = 'move'
      }}
      className={cn('absolute inset-x-1 z-10 overflow-hidden rounded-[var(--lc-radius-sm)] border-s-2 bg-[var(--lc-surface-raised)] p-1 shadow-[var(--lc-elevation-sm)]', cancelled && 'opacity-60')}
      style={{ ...pos, borderInlineStartColor: colour }}
      aria-label={event.title}
    >
      <span className="flex items-center gap-0.5">
        <KindIcon className="h-3 w-3 shrink-0" style={{ color: colour }} aria-hidden="true" />
        <span className="min-w-0 truncate" style={{ font: 'var(--lc-type-caption)' }}>
          <span className={cn(cancelled && 'line-through')}>{event.title}</span>
        </span>
        {event.is_recurrence_instance ? <SeriesGlyph locale={locale} className="text-[var(--lc-text-muted)]" /> : null}
      </span>
      <span className="tabular-nums text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
        {new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(start)}
      </span>
      {event.status === 'pending' ? <Badge variant="underOffer">{t('calendar.status.pending', locale)}</Badge> : null}
      {cancelled ? <Badge variant="unpublished">{t('calendar.status.cancelled', locale)}</Badge> : null}
      <span className="flex items-center">
        <IconAction label={t('calendar.action.reschedule', locale)} onClick={() => onReschedule(event)}>
          <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
        </IconAction>
        <IconAction label={t('calendar.action.cancel', locale)} onClick={() => onCancel(event)}>
          <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
        </IconAction>
        <IconAction
          label={event.contact_id ? t('calendar.action.message', locale) : t('calendar.noLinkedContact', locale)}
          disabled={!event.contact_id}
          onClick={() => onSendMessage(event)}
        >
          <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
        </IconAction>
        <IconAction label={t('calendar.action.reassign', locale)} onClick={() => onMemberAction(event, 'reassign')}>
          <UserRoundPlus className="h-3.5 w-3.5" aria-hidden="true" />
        </IconAction>
        <IconAction label={t('calendar.action.delegate', locale)} onClick={() => onMemberAction(event, 'delegate')}>
          <UserRoundCheck className="h-3.5 w-3.5" aria-hidden="true" />
        </IconAction>
      </span>
    </div>
  )
}

function EventChip({
  event,
  locale,
  onReschedule,
  onCancel,
  onSendMessage,
  onMemberAction,
}: {
  event: CalendarEvent
  locale: DashboardLocale
  onReschedule: (event: CalendarEvent) => void
  onCancel: (event: CalendarEvent) => void
  onSendMessage: (event: CalendarEvent) => void
  onMemberAction: (event: CalendarEvent, mode: MemberActionMode) => void
}) {
  const KindIcon = KIND_ICONS[event.kind] ?? KIND_ICONS.custom
  const colour = KIND_COLORS[event.kind]
  const cancelled = event.status === 'cancelled'
  return (
    <span
      data-testid={`chip-${event.id}`}
      className={cn('mb-0.5 flex items-center gap-1 rounded-[var(--lc-radius-sm)] px-1', cancelled && 'opacity-60')}
      style={{ font: 'var(--lc-type-caption)' }}
    >
      <KindIcon className="h-3 w-3 shrink-0" style={{ color: colour }} aria-hidden="true" />
      <span className={cn('min-w-0 flex-1 truncate', cancelled && 'line-through')}>{event.title}</span>
      {event.is_recurrence_instance ? <SeriesGlyph locale={locale} className="text-[var(--lc-text-muted)]" /> : null}
      {event.status === 'pending' ? <Badge variant="underOffer">{t('calendar.status.pending', locale)}</Badge> : null}
      {cancelled ? <Badge variant="unpublished">{t('calendar.status.cancelled', locale)}</Badge> : null}
      <IconAction label={t('calendar.action.reschedule', locale)} onClick={() => onReschedule(event)}>
        <CalendarClock className="h-3 w-3" aria-hidden="true" />
      </IconAction>
      <IconAction label={t('calendar.action.cancel', locale)} onClick={() => onCancel(event)}>
        <XCircle className="h-3 w-3" aria-hidden="true" />
      </IconAction>
      <IconAction
        label={event.contact_id ? t('calendar.action.message', locale) : t('calendar.noLinkedContact', locale)}
        disabled={!event.contact_id}
        onClick={() => onSendMessage(event)}
      >
        <MessageSquare className="h-3 w-3" aria-hidden="true" />
      </IconAction>
      <IconAction label={t('calendar.action.reassign', locale)} onClick={() => onMemberAction(event, 'reassign')}>
        <UserRoundPlus className="h-3 w-3" aria-hidden="true" />
      </IconAction>
      <IconAction label={t('calendar.action.delegate', locale)} onClick={() => onMemberAction(event, 'delegate')}>
        <UserRoundCheck className="h-3 w-3" aria-hidden="true" />
      </IconAction>
    </span>
  )
}

/**
 * Wave 5 — "Who's where today": one row per team member (self excluded) with
 * events on the anchor day. Doubles as the colour legend — the member dot
 * uses the same memberColor() hash as the grid blocks/chips.
 */
function TeamStrip({
  members,
  events,
  anchorKey,
  locale,
  failed,
}: {
  members: CalendarTeamEventsResponse['members']
  events: TeamCalendarEvent[]
  anchorKey: string
  locale: DashboardLocale
  failed: boolean
}) {
  const timeLabel = (event: TeamCalendarEvent): string => {
    if (event.all_day) return t('calendar.allDay', locale)
    const d = new Date(event.starts_at)
    if (Number.isNaN(d.getTime())) return ''
    return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(d)
  }
  if (failed) {
    return (
      <section data-testid="team-strip" role="alert" className="mb-[var(--lc-space-sm)] rounded-[var(--lc-radius-sm)] border border-[var(--lc-status-unpublished-fg)] p-[var(--lc-space-sm)] text-[var(--lc-status-unpublished-fg)]" style={{ font: 'var(--lc-type-body-sm)' }}>
        {t('calendar.team.error', locale)}
      </section>
    )
  }
  const rows = members
    .map((member) => ({
      member,
      dayEvents: events.filter((e) => dayKey(new Date(e.starts_at)) === anchorKey),
    }))
    .filter((row) => row.dayEvents.length > 0)
  return (
    <section data-testid="team-strip" className="mb-[var(--lc-space-sm)] rounded-[var(--lc-radius-sm)] border border-[var(--lc-border)] bg-[var(--lc-surface-sunken)] p-[var(--lc-space-sm)]">
      <h3 className="mb-1 text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-overline)', letterSpacing: 'var(--lc-tracking-overline)' }}>
        {t('calendar.team.stripTitle', locale)}
      </h3>
      {rows.length === 0 ? (
        <p data-testid="team-strip-empty" className="text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-body-sm)' }}>
          {t('calendar.team.empty', locale)}
        </p>
      ) : (
        <ul>
          {rows.map(({ member, dayEvents }) => (
            <li key={member.user_id} data-testid={`team-member-${member.user_id}`} className="flex flex-wrap items-baseline gap-x-[var(--lc-space-sm)] py-0.5">
              <span className="inline-flex items-center gap-[var(--lc-space-2xs)]" style={{ font: 'var(--lc-type-body-sm)' }}>
                <span className="h-2 w-2 rounded-pill" style={{ backgroundColor: memberColor(member.user_id) }} aria-hidden="true" />
                {member.name}
              </span>
              <span className="text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
                {dayEvents.map((e) => [timeLabel(e), e.title].filter(Boolean).join(' ')).join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** Wave 5 — a colleague's timed event: read-only, de-emphasised, member colour. */
function TeamEventBlock({
  event,
  spanMinutes,
  locale,
}: {
  event: TeamCalendarEvent
  spanMinutes: number
  locale: DashboardLocale
}) {
  const colour = memberColor(event.owner_user_id)
  const pos = eventPosition(event, spanMinutes)
  const start = new Date(event.starts_at)
  return (
    <div
      data-testid={`team-event-${event.id}`}
      className="absolute inset-x-1 z-0 overflow-hidden rounded-[var(--lc-radius-sm)] border-s-2 bg-[var(--lc-surface-sunken)] p-1 opacity-70"
      style={{ ...pos, borderInlineStartColor: colour }}
      aria-label={event.title}
    >
      <span className="flex items-center gap-0.5">
        <span className="h-1.5 w-1.5 shrink-0 rounded-pill" style={{ backgroundColor: colour }} aria-hidden="true" />
        <span className="min-w-0 truncate" style={{ font: 'var(--lc-type-caption)' }}>{event.title}</span>
      </span>
      <span className="tabular-nums text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
        {new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(start)}
      </span>
      <span className="block truncate text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
        {event.owner_name ?? t('calendar.team.memberFallback', locale)}
      </span>
      {event.status === 'pending' ? <Badge variant="underOffer">{t('calendar.status.pending', locale)}</Badge> : null}
      {event.status === 'cancelled' ? <Badge variant="unpublished">{t('calendar.status.cancelled', locale)}</Badge> : null}
    </div>
  )
}

/** Wave 5 — a colleague's multi-day / all-day event chip: read-only. */
function TeamEventChip({ event, locale }: { event: TeamCalendarEvent; locale: DashboardLocale }) {
  const colour = memberColor(event.owner_user_id)
  const cancelled = event.status === 'cancelled'
  return (
    <span
      data-testid={`team-chip-${event.id}`}
      className={cn('mb-0.5 flex items-center gap-1 rounded-[var(--lc-radius-sm)] px-1 opacity-70', cancelled && 'line-through')}
      style={{ font: 'var(--lc-type-caption)' }}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-pill" style={{ backgroundColor: colour }} aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{event.title}</span>
      <span className="shrink-0 text-[var(--lc-text-muted)]">{event.owner_name ?? t('calendar.team.memberFallback', locale)}</span>
      {event.status === 'pending' ? <Badge variant="underOffer">{t('calendar.status.pending', locale)}</Badge> : null}
      {cancelled ? <Badge variant="unpublished">{t('calendar.status.cancelled', locale)}</Badge> : null}
    </span>
  )
}

function ScheduleList({
  groups,
  locale,
  onReschedule,
  onCancel,
  onSendMessage,
  onMemberAction,
}: {
  groups: Array<[string, CalendarEvent[]]>
  locale: DashboardLocale
  onReschedule: (event: CalendarEvent) => void
  onCancel: (event: CalendarEvent) => void
  onSendMessage: (event: CalendarEvent) => void
  onMemberAction: (event: CalendarEvent, mode: MemberActionMode) => void
}) {
  const todayKey = dayKey(new Date())
  const dateLabel = (key: string): string => {
    if (key === todayKey) return t('calendar.today', locale)
    const tomorrow = startOfDay(new Date())
    tomorrow.setDate(tomorrow.getDate() + 1)
    if (key === dayKey(tomorrow)) return t('calendar.tomorrow', locale)
    return new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(`${key}T00:00:00`))
  }
  const timeLabel = (event: CalendarEvent): string => {
    if (event.all_day) return t('calendar.allDay', locale)
    const d = new Date(event.starts_at)
    if (Number.isNaN(d.getTime())) return ''
    return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(d)
  }
  return (
    <div data-testid="diary-schedule" className="space-y-[var(--lc-space-md)]">
      {groups.map(([key, group]) => (
        <section key={key}>
          <h4 className="mb-1 text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-overline)', letterSpacing: 'var(--lc-tracking-overline)' }}>
            {dateLabel(key)}
          </h4>
          <ul>
            {group.map((event) => {
              const KindIcon = KIND_ICONS[event.kind] ?? KIND_ICONS.custom
              const colour = KIND_COLORS[event.kind]
              const cancelled = event.status === 'cancelled'
              return (
                <li
                  key={event.id}
                  data-testid={`event-${event.id}`}
                  className="flex items-center gap-[var(--lc-space-sm)] border-b border-[var(--lc-border)] py-[var(--lc-space-2xs)]"
                >
                  <span className="w-14 shrink-0 tabular-nums text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
                    {timeLabel(event)}
                  </span>
                  <span className="h-2 w-2 shrink-0 rounded-pill" style={{ backgroundColor: colour }} aria-hidden="true" />
                  <KindIcon className="h-4 w-4 shrink-0" style={{ color: colour }} aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    {event.is_recurrence_instance ? (
                      <span className="flex items-center gap-[var(--lc-space-2xs)]">
                        <span className={cn('block truncate', cancelled && 'text-[var(--lc-text-muted)] line-through')} style={{ font: 'var(--lc-type-body-sm)' }}>
                          {event.title}
                        </span>
                        <SeriesGlyph locale={locale} className="text-[var(--lc-text-muted)]" />
                      </span>
                    ) : (
                      <span className={cn('block truncate', cancelled && 'text-[var(--lc-text-muted)] line-through')} style={{ font: 'var(--lc-type-body-sm)' }}>
                        {event.title}
                      </span>
                    )}
                    {event.description ? (
                      <span className={cn('block truncate text-[var(--lc-text-muted)]', cancelled && 'line-through')} style={{ font: 'var(--lc-type-caption)' }}>
                        {event.description}
                      </span>
                    ) : null}
                  </span>
                  {event.status === 'pending' ? <Badge variant="underOffer">{t('calendar.status.pending', locale)}</Badge> : null}
                  {cancelled ? <Badge variant="unpublished">{t('calendar.status.cancelled', locale)}</Badge> : null}
                  <span className="flex shrink-0 items-center">
                    <IconAction label={t('calendar.action.reschedule', locale)} onClick={() => onReschedule(event)}>
                      <CalendarClock className="h-4 w-4" aria-hidden="true" />
                    </IconAction>
                    <IconAction label={t('calendar.action.cancel', locale)} onClick={() => onCancel(event)}>
                      <XCircle className="h-4 w-4" aria-hidden="true" />
                    </IconAction>
                    <IconAction
                      label={event.contact_id ? t('calendar.action.message', locale) : t('calendar.noLinkedContact', locale)}
                      disabled={!event.contact_id}
                      onClick={() => onSendMessage(event)}
                    >
                      <MessageSquare className="h-4 w-4" aria-hidden="true" />
                    </IconAction>
                    <IconAction label={t('calendar.action.reassign', locale)} onClick={() => onMemberAction(event, 'reassign')}>
                      <UserRoundPlus className="h-4 w-4" aria-hidden="true" />
                    </IconAction>
                    <IconAction label={t('calendar.action.delegate', locale)} onClick={() => onMemberAction(event, 'delegate')}>
                      <UserRoundCheck className="h-4 w-4" aria-hidden="true" />
                    </IconAction>
                  </span>
                </li>
              )
            })}
          </ul>
        </section>
      ))}
    </div>
  )
}

function IconAction({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled?: boolean
  onClick?: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-tap min-h-tap w-tap min-w-tap items-center justify-center rounded-[var(--lc-radius-md)] text-[var(--lc-text-muted)] transition-colors hover:bg-[var(--lc-surface-sunken)] hover:text-[var(--lc-text-primary)] disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  )
}
